"use client"

import { useCategory } from "@/hook/useCategory"
import { CategoryData } from "@/lib/config/types"
import { filterOption } from "@/lib/external-components"
import { Button, Select, Typography } from "antd"
import { useCallback, useEffect, useMemo, useState } from "react"

interface CategoryResponse {
  data?: CategoryData[]
  meta?: {
    pagination?: {
      page?: number
      pageSize?: number
      total?: number
    }
  }
  error?: {
    message: string
  }
}

export type CategorySelectValue =
  | {
      categoryId: string
      categoryName?: string
      slug?: string
      playlistUrl?: string
    }
  | string

interface SelectCategoryProps {
  value?: CategorySelectValue
  onChange?: (value: CategorySelectValue) => void
  Label?: string | (() => React.ReactNode)
  [key: string]: any
}

export const SelectCategory = ({ value, onChange, Label, ...selectProps }: SelectCategoryProps) => {
  const { fetchCategories, loading: hookLoading } = useCategory()
  const [allCategories, setAllCategories] = useState<CategoryData[]>([])
  const [currentPage, setCurrentPage] = useState(1)
  const [hasMore, setHasMore] = useState(true)
  const [loading, setLoading] = useState(false)

  const loadCategories = useCallback(
    async (page: number) => {
      try {
        setLoading(true)
        const response = (await fetchCategories({ page: String(page), pageSize: "5" }, "preview")) as
          | CategoryResponse
          | undefined

        if (response?.data) {
          setAllCategories(prev => {
            // Tránh duplicate bằng cách lọc theo id
            const existingIds = new Set(prev.map(cat => cat.id))
            const newCategories = (response.data || []).filter((cat: CategoryData) => !existingIds.has(cat.id))
            return [...prev, ...newCategories]
          })

          // Kiểm tra còn trang nữa không
          const total = response?.meta?.pagination?.total ?? 0
          const pageSize = response?.meta?.pagination?.pageSize ?? 5
          setHasMore(page * pageSize < total)
        }
      } catch (error) {
        console.error("Failed to load categories:", error)
      } finally {
        setLoading(false)
      }
    },
    [fetchCategories],
  )

  // Load trang đầu tiên khi mount
  useEffect(() => {
    loadCategories(1)
  }, [])

  const handleLoadMore = () => {
    const nextPage = currentPage + 1
    setCurrentPage(nextPage)
    loadCategories(nextPage)
  }

  const options = useMemo(() => {
    const categoryOptions = allCategories.map((category: CategoryData) => ({
      value: String(category.id),
      label: category.attributes.name,
    }))
    return [{ value: "all", label: "All Categories" }, ...categoryOptions]
  }, [allCategories])

  const handleChange = (val: string) => {
    if (val === "all") {
      onChange?.("")
      return
    }
    const selectedCategory = allCategories.find(cat => String(cat.id) === val)
    if (selectedCategory && onChange) {
      onChange({
        categoryId: val,
        categoryName: selectedCategory.attributes.name,
        slug: selectedCategory.attributes.slug,
        playlistUrl: (selectedCategory.attributes as any).playlistUrl,
      })
    }
  }

  const displayValue = useMemo(() => {
    if (typeof value === "string") {
      return value === "" || value === undefined ? "all" : value
    }
    return value?.categoryId === "" || !value?.categoryId ? "all" : value.categoryId
  }, [value])

  const labelText = typeof Label === "function" ? "Select Category" : Label || "Select Category"

  return (
    <div>
      <Typography.Title level={5}>{labelText}</Typography.Title>
      <Select
        {...selectProps}
        showSearch
        placeholder="Chọn category"
        style={{ minWidth: "14rem", ...selectProps.style }}
        optionFilterProp="children"
        filterOption={filterOption}
        options={options}
        value={displayValue}
        onChange={handleChange}
        loading={loading || hookLoading}
        popupRender={menu => (
          <>
            {menu}
            {hasMore && (
              <Button
                onClick={handleLoadMore}
                loading={loading}
                style={{ width: "100%", marginTop: 8 }}
                type="dashed"
                size="small"
              >
                Xem thêm
              </Button>
            )}
          </>
        )}
      />
    </div>
  )
}
